/**
 * src/modules/nip-vies.ts — NIP/VIES validation + UI inline lookup.
 * P3.2 strict TS migration (2026-04-30).
 *
 * Pierwszy moduł zmigrowany z IIFE na native ES modules.
 * Backward-compat: window.X exposures zachowane na końcu pliku.
 */

// ── Public types ─────────────────────────────────────────────────────────

/** Order data shape — minimum needed dla NIP comparison. */
export interface OrderNipData {
  id?: string | number;
  invoice_tax_id?: string;
  invoice_company_name?: string;
  nip_mf_name?: string;
  nip_status?: string;
  [key: string]: unknown;
}

export interface NipLookupData {
  name?: string;
  regon?: string;
  address?: string;
  status_vat?: string;
  account_numbers?: string[];
  [key: string]: unknown;
}

export interface ViesCheckResult {
  valid?: boolean;
  name?: string;
  error?: string;
}

// SellBaseAPI (legacy global, w app.js IIFE)
interface SellBaseAPILike {
  verifyNip: (nip: string) => Promise<NipLookupData>;
  checkVies: (nip: string) => Promise<ViesCheckResult>;
  request: (method: string, url: string, body?: unknown) => Promise<unknown>;
}

declare const SellBaseAPI: SellBaseAPILike;

// Funkcje globalne z innych modułów (legacy delegate via window)
declare function escapeAttr(s: unknown): string;
declare function escapeHtml(s: unknown): string;
declare function showToast(msg: string, type?: string): void;

// Lucide CDN (też dostępny via globalThis.lucide)
declare const lucide: { createIcons: (opts?: { nodes?: Element[] }) => void };

// HTMLElement extension dla custom property
interface ViesBadgeElement extends HTMLElement {
  _viesHydrated?: boolean;
}

// ── State proxy (load-order safe) ───────────────────────────────────────
// Faza E.1 fix: lazy state proxy — żeby ten moduł nie crashował gdy ładowany przed app.js.
// Currently unused w tym pliku ale eksport dla legacy code który może to importować.
// (Removed unused warning by void cast)
const _state: Record<string | number, unknown> = (typeof window !== "undefined" && (window.__sbState as Record<string, unknown> | undefined)) || new Proxy({} as Record<string | number, unknown>, {
  get: (_t, k: string | symbol): unknown => {
    if (typeof k === "symbol") return undefined;
    const sb = window.__sbState as Record<string, unknown> | undefined;
    return sb ? sb[k] : undefined;
  },
  set: (_t, k: string | symbol, v: unknown): boolean => {
    if (typeof k === "symbol") return true;
    const sb = window.__sbState as Record<string, unknown> | undefined;
    if (sb) sb[k] = v;
    return true;
  },
  has: (_t, k: string | symbol): boolean => {
    if (typeof k === "symbol") return false;
    const sb = window.__sbState as Record<string, unknown> | undefined;
    return sb ? k in sb : false;
  },
});
void _state; // mark as used (legacy compat)

// ── Implementation ──────────────────────────────────────────────────────

function _checkOrderNipStatus(order: OrderNipData | null | undefined): void {
  const orderData: OrderNipData = order || {};
  const nip = orderData.invoice_tax_id || "";
  if (!nip || nip.replace(/\D/g, "").length !== 10) return;

  const companyBadge = document.getElementById("rd-nip-company-badge");
  const vatBadge = document.getElementById("rd-nip-vat-badge");
  const mfName = orderData.nip_mf_name || "";
  const nipStatus = orderData.nip_status || "";

  // If already verified (from sync), show comparison
  if (nipStatus && mfName) {
    _renderNipComparison(orderData, mfName, nipStatus, companyBadge, vatBadge);
    return;
  }

  // Not yet verified — show "Zweryfikuj" link
  if (vatBadge) {
    vatBadge.innerHTML =
      '<a href="#" id="rd-verify-nip-link" style="font-size:10px;color:var(--color-primary);text-decoration:none">Zweryfikuj</a>';
    const link = document.getElementById("rd-verify-nip-link");
    if (link) {
      link.addEventListener("click", function (this: HTMLElement, e: Event): void {
        e.preventDefault();
        this.textContent = "...";
        SellBaseAPI.verifyNip(nip)
          .then((data: NipLookupData) => {
            _renderNipComparison(orderData, data.name || "", data.status_vat || "", companyBadge, vatBadge);
          })
          .catch(() => {
            if (vatBadge)
              vatBadge.innerHTML = '<span style="font-size:10px;color:var(--color-text-muted)">Brak danych</span>';
          });
      });
    }
  }
}

function _renderNipComparison(
  orderData: OrderNipData,
  mfName: string,
  nipStatus: string,
  companyBadge: HTMLElement | null,
  vatBadge: HTMLElement | null,
): void {
  // VAT badge
  if (vatBadge) vatBadge.innerHTML = _vatBadgeHtml(nipStatus);

  // Company name comparison
  if (companyBadge && mfName && orderData.invoice_company_name) {
    const allegroName = (orderData.invoice_company_name || "").toUpperCase().trim();
    const regName = (mfName || "").toUpperCase().trim();
    const normalize = (s: string): string => s.replace(/\./g, "").replace(/\s+/g, " ").trim();
    const match =
      normalize(allegroName) === normalize(regName) ||
      normalize(allegroName).indexOf(normalize(regName)) >= 0 ||
      normalize(regName).indexOf(normalize(allegroName)) >= 0;

    if (match) {
      companyBadge.innerHTML = '<i data-lucide="check-circle" style="width:12px;height:12px;color:#16A34A"></i>';
    } else {
      companyBadge.innerHTML =
        '<span style="display:inline-flex;align-items:center;gap:4px">' +
        '<i data-lucide="alert-triangle" style="width:12px;height:12px;color:#F59E0B"></i>' +
        '<span style="font-size:9px;color:#F59E0B" title="Wg rejestru MF: ' +
        escapeAttr(mfName) +
        '">Inne dane w rejestrze</span>' +
        '<button id="rd-fix-nip-btn" style="font-size:9px;padding:1px 6px;border-radius:3px;border:1px solid #F59E0B;color:#F59E0B;background:none;cursor:pointer;margin-left:2px">Popraw dane</button>' +
        "</span>";
      lucide.createIcons();

      // Bind "Popraw dane" button
      setTimeout(() => {
        const fixBtn = document.getElementById("rd-fix-nip-btn");
        if (fixBtn) {
          fixBtn.addEventListener("click", () => {
            const orderId = orderData.id;
            if (!orderId) return;
            // Update order invoice_company_name to match MF data
            SellBaseAPI.request("PUT", "/api/orders/" + encodeURIComponent(String(orderId)), {
              invoice_company_name: mfName,
            })
              .then(() => {
                showToast("Nazwa firmy zaktualizowana z rejestru MF", "success");
                if (companyBadge)
                  companyBadge.innerHTML =
                    '<i data-lucide="check-circle" style="width:12px;height:12px;color:#16A34A"></i>';
                lucide.createIcons();
                // Update display
                const companyDiv = companyBadge?.parentElement;
                if (companyDiv) {
                  const textNode = companyDiv.firstChild;
                  if (textNode && textNode.nodeType === 3) textNode.textContent = mfName;
                }
              })
              .catch((err: { message?: string }) => {
                showToast(err.message || "Nie udało się zaktualizować danych", "error");
              });
          });
        }
      }, 30);
    }
    lucide.createIcons();
  }

  // Nieczynny VAT warning
  if (nipStatus && nipStatus !== "Czynny") {
    const nipLine = vatBadge ? vatBadge.parentElement : null;
    if (nipLine && !document.getElementById("rd-vat-warning")) {
      const warn = document.createElement("div");
      warn.id = "rd-vat-warning";
      warn.style.cssText =
        "margin-top:4px;padding:4px 8px;border-radius:4px;background:#FEE2E2;font-size:10px;color:#DC2626;font-weight:600";
      warn.textContent = "⚠ Podmiot " + (nipStatus === "Zwolniony" ? "zwolniony z VAT" : "nieczynny VAT") + "!";
      if (nipLine.parentElement) nipLine.parentElement.appendChild(warn);
    }
  }
}

// ── NIP Lookup — reużywalny inline button ───────────────────────────────

type NipResultCallback = (data: NipLookupData) => void;

function _nipInitInlineButton(nipInputId: string, onResult?: NipResultCallback): void {
  const input = document.getElementById(nipInputId) as HTMLInputElement | null;
  if (!input || input.dataset.nipInit) return;
  input.dataset.nipInit = "1";

  // Wrapper musi mieć position:relative
  const wrapper = input.parentElement;
  if (wrapper) wrapper.style.position = "relative";

  // Button wewnątrz inputa (po prawej)
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "nip-lookup-btn";
  btn.title = "Pobierz dane z GUS";
  btn.innerHTML = '<i data-lucide="search" style="width:14px;height:14px"></i>';
  btn.style.cssText =
    "position:absolute;right:8px;top:50%;transform:translateY(-50%);background:none;border:none;cursor:pointer;color:var(--color-text-muted);padding:4px;border-radius:4px;display:flex;align-items:center;transition:color .2s";
  if (wrapper) wrapper.appendChild(btn);
  // Adjust input padding
  input.style.paddingRight = "36px";

  // Badge container pod inputem
  const badgeEl = document.createElement("div");
  badgeEl.className = "nip-vat-badge";
  badgeEl.style.cssText = "margin-top:4px;font-size:11px;display:none";
  const formGroup = wrapper ? wrapper.parentElement : null;
  if (formGroup) formGroup.appendChild(badgeEl);

  btn.addEventListener("click", () => {
    const nip = (input.value || "").replace(/\D/g, "");
    if (nip.length !== 10) {
      showToast("NIP musi składać się z 10 cyfr", "error");
      return;
    }
    // Spinner
    btn.innerHTML = '<i data-lucide="loader" style="width:14px;height:14px;animation:spin 1s linear infinite"></i>';
    btn.disabled = true;
    lucide.createIcons();

    SellBaseAPI.verifyNip(nip)
      .then((data: NipLookupData) => {
        btn.innerHTML =
          '<i data-lucide="check" style="width:14px;height:14px;color:var(--color-success)"></i>';
        btn.disabled = false;
        lucide.createIcons();

        // Badge VAT
        const vatColor =
          data.status_vat === "Czynny"
            ? "#16A34A"
            : data.status_vat === "Zwolniony"
              ? "#9CA3AF"
              : "#EF4444";
        const vatLabel = data.status_vat === "Czynny" ? "VAT Czynny" : data.status_vat || "Nieznany";
        badgeEl.innerHTML =
          '<span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:4px;background:' +
          vatColor +
          "15;color:" +
          vatColor +
          ';font-weight:600"><i data-lucide="' +
          (data.status_vat === "Czynny" ? "check-circle" : "alert-circle") +
          '" style="width:12px;height:12px"></i>' +
          escapeHtml(vatLabel) +
          "</span>";
        badgeEl.style.display = "block";
        lucide.createIcons();

        showToast("Dane pobrane: " + (data.name || "").substring(0, 60), "success");
        if (onResult) onResult(data);

        // Reset icon after 3s
        setTimeout(() => {
          btn.innerHTML = '<i data-lucide="search" style="width:14px;height:14px"></i>';
          lucide.createIcons();
        }, 3000);
      })
      .catch((err: { message?: string }) => {
        btn.innerHTML = '<i data-lucide="search" style="width:14px;height:14px"></i>';
        btn.disabled = false;
        badgeEl.style.display = "none";
        lucide.createIcons();
        // Nieudane pobranie nie blokuje automatu — kolejna zmiana NIP-u spróbuje znowu.
        delete input.dataset.nipLast;
        showToast(err.message || "Nie udało się zweryfikować NIP", "error");
      });
  });

  lucide.createIcons();
}

// ── VIES badge — sprawdzenie NIP UE ─────────────────────────────────────

const _PREFIKSY_VAT_UE = ["AT", "BE", "BG", "CY", "CZ", "DE", "DK", "EE", "EL", "ES", "FI", "FR", "GR", "HR", "HU",
  "IE", "IT", "LT", "LU", "LV", "MT", "NL", "PL", "PT", "RO", "SE", "SI", "SK"];

/** Numer VAT-UE (z prefiksem) z numeru wpisanego przez nabywcę — bliźniak `core/vat_ue_format.normalizuj_vat_ue`
 *  dla widoku: węgierski 11-cyfrowy adószám (29168967207) → HU + pierwsze 8 cyfr (HU29168967). '' poza UE. */
function _numerVatUe(nipRaw: unknown, countryHint?: unknown): string {
  const n = String(nipRaw || "").replace(/[\s.\-]/g, "").toUpperCase();
  const pWejscia = n.length >= 3 && _PREFIKSY_VAT_UE.indexOf(n.slice(0, 2)) >= 0 ? n.slice(0, 2) : "";
  let reszta = pWejscia ? n.slice(2) : n;
  const kraj = String(countryHint || "").trim().toUpperCase();
  const prefiks = pWejscia || (kraj === "GR" ? "EL" : kraj);
  if (!prefiks || !reszta || _PREFIKSY_VAT_UE.indexOf(prefiks) < 0) return "";
  if (prefiks === "HU") {
    const cyfry = reszta.replace(/\D/g, "");
    reszta = cyfry.length === 11 ? cyfry.slice(0, 8) : (cyfry || reszta);
  }
  return prefiks + reszta;
}

/** 27.10.2026 (właściciel): VIES na stronie Komisji pokazał „nieważny” dla 29168967207, a SellBase „VIES OK” — bez
 *  słowa wyjaśnienia, że sprawdzany jest HU29168967. Gdy numer VAT-UE różni się od wpisanego, pokazujemy go obok
 *  NIP-u (zamówienie, faktura, lista faktur) razem z tym, co wpisać ręcznie w VIES. */
function _notkaVatUe(nipRaw: unknown, countryHint?: unknown, kompaktowa?: boolean): string {
  const pelny = _numerVatUe(nipRaw, countryHint);
  if (!pelny || pelny.startsWith("PL")) return "";
  const wpisany = String(nipRaw || "").replace(/[\s.\-]/g, "").toUpperCase();
  if (pelny === wpisany || pelny.slice(2) === wpisany) return "";
  if (kompaktowa) {
    return '<div style="font-size:10px;color:var(--color-text-muted)">VAT-UE: <b>' + escapeHtml(pelny) + "</b></div>";
  }
  const powod = pelny.startsWith("HU")
    ? "węgierski numer podatkowy (adószám) ma 11 cyfr, a w VIES i na fakturze WDT jest HU + pierwsze 8 cyfr"
    : "w VIES i na fakturze jest numer w formacie VAT-UE";
  return (
    '<span class="vat-ue-notka" style="display:block;margin-top:3px;font-size:11px;font-weight:500;line-height:1.4;color:var(--color-text-muted)">' +
    '<i data-lucide="info" style="width:11px;height:11px;vertical-align:-1px"></i> Numer VAT-UE: <b style="color:var(--color-text)">' +
    escapeHtml(pelny) + "</b> — " + powod + ". Sprawdzając ręcznie w VIES, wpisz " + escapeHtml(pelny.slice(0, 2)) +
    " i <b>" + escapeHtml(pelny.slice(2)) + "</b>.</span>"
  );
}

function _buildViesBadgePlaceholder(nipRaw: unknown, countryHint?: unknown): string {
  const nip = String(nipRaw || "").replace(/\s+|-/g, "").trim().toUpperCase();
  if (!nip) return "";
  const country = String(countryHint || "PL").toUpperCase();
  // 14.10.2026 (#117688): numer z literami (NL…B42, ATU…) — wcześniej prefiks „nie pasował” i do VIES szedł kraj + same
  // cyfry (NL00550409642), więc plakietka pokazywała „VIES nieaktywny” dla aktywnego numeru.
  const hasPrefix = /^[A-Z]{2}[0-9A-Z]*\d[0-9A-Z]*$/.test(nip);
  // 2026-09-09 (decyzja właściciela): nabywca z Polski — bez VIES i bez plakietki. VIES potwierdza tylko
  // rejestrację VAT-UE, której większość krajowych firm nie ma (fałszywe „nieaktywny" przy Kapsello).
  if (nip.startsWith("PL") || (!hasPrefix && country === "PL")) return "";
  const surowy = hasPrefix ? nip : country + nip.replace(/\D/g, "");
  const fullVat = _numerVatUe(surowy) || surowy;
  return (
    '<span class="vies-badge-pending" data-vies-nip="' +
    escapeAttr(fullVat) +
    '" style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:10px;background:var(--color-surface-2);border:1px solid var(--color-border);color:var(--color-text-muted);font-size:10px;font-weight:600;margin-left:6px;vertical-align:middle">' +
    '<i data-lucide="loader-2" style="width:10px;height:10px;animation:spin 1s linear infinite"></i> VIES…</span>' +
    _notkaVatUe(nip, country)
  );
}

/** Plakietka VIES DOKUMENTU: wynik zapisany przy wystawieniu (documents.vies_*) albo sprawdzenie
 *  na żywo, gdy dokument nie ma zapisanego wyniku (starsze dokumenty, seria bez VIES).
 *  2026-09-09: dotąd strona faktury pokazywała „VIES OK" tylko dlatego, że seria ma VIES włączony
 *  (WDT/007/09/2026 wyszło 0 % bez sprawdzenia; zamówienie pokazywało „VIES nieaktywny"). */
function _viesPillDokumentu(doc: any): string {
  if (!doc || !doc.buyer_tax_no) return "";
  // nabywca z Polski: VIES nie dotyczy — żadnej informacji o VIES (decyzja właściciela 2026-09-09)
  const krajDok = String(doc.buyer_country || "PL").toUpperCase();
  if (krajDok === "PL" || String(doc.buyer_tax_no).replace(/\s+|-/g, "").toUpperCase().startsWith("PL")) return "";
  // 14.10.2026 (#117688): wynik VIES zapisany dla INNEGO numeru (np. obciętego NL005504096 sprzed poprawki) nie dotyczy
  // obecnego numeru — szkic świecił „VIES nieaktywny”, choć poprawiony numer jest aktywny. Wtedy jak bez wyniku.
  // 27.10.2026: porównanie numerów VAT-UE, nie surowych — węgierski 29168967207 to HU29168967 z wyniku VIES
  // (WDT/055/10/2026 pokazywało „Brak dowodu VIES”, choć dowód z dnia wystawienia jest zapisany).
  const numerNabywcy = doc.buyer_eu_vat_no || doc.buyer_tax_no;
  const _bezPref = (x: unknown) => {
    const pelny = _numerVatUe(x, krajDok);
    return pelny ? pelny.slice(2) : String(x || "").replace(/[\s.\-]/g, "").toUpperCase().replace(/^[A-Z]{2}(?=[0-9A-Z]*\d)/, "");
  };
  const _innyNumer = !!doc.vies_number && _bezPref(doc.vies_number) !== _bezPref(numerNabywcy);
  const st = _innyNumer ? "" : String(doc.vies_status || "");
  const kiedy = doc.vies_checked_at ? formatDateTime(doc.vies_checked_at) : "";
  const nr = String(doc.vies_number || numerNabywcy || "");
  const notka = _notkaVatUe(doc.buyer_tax_no, krajDok);
  const pill = (tekst: string, kolor: string, tlo: string, ikona: string, tytul: string) =>
    '<span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:10px;background:' + tlo + ';color:' + kolor + ';font-size:10px;font-weight:600;margin-left:6px;vertical-align:middle" title="' + escapeAttr(tytul) + '"><i data-lucide="' + ikona + '" style="width:10px;height:10px"></i> ' + tekst + '</span>' + notka;
  if (st === "valid") return pill("VIES OK", "#22c55e", "rgba(34,197,94,.15)", "shield-check", "Numer " + nr + " aktywny w VIES przy wystawieniu" + (kiedy ? " (" + kiedy + ")" : "") + (doc.vies_name ? " — " + doc.vies_name : ""));
  if (st === "invalid") return pill("VIES NIEAKTYWNY", "#ef4444", "rgba(239,68,68,.15)", "shield-x", "VIES nie potwierdził numeru " + nr + (kiedy ? " (" + kiedy + ")" : ""));
  if (st === "unavailable") return pill("VIES niedostępny", "var(--color-text-muted)", "var(--color-surface-2)", "help-circle", "Przy wystawianiu VIES nie odpowiedział" + (kiedy ? " (" + kiedy + ")" : ""));
  // P2 C11 (2026-09-17): dokument WDT WYSTAWIONY bez zapisanego wyniku VIES nie może pokazywać
  // plakietki ze sprawdzenia NA ŻYWO. 18 starych WDT pokazywało „VIES OK” tylko dlatego, że numer
  // jest dziś aktywny — a stawka 0 % wymaga dowodu z DNIA DOSTAWY, nie z dzisiaj. Sprawdzenie na
  // żywo zostaje dostępne, ale pod własną etykietą (baner szkicu / przycisk „Sprawdź VIES teraz”).
  const wdt = String(doc.document_type || "").toLowerCase() === "wdt"
    || String(doc.tax_procedure_type || "").toLowerCase() === "wdt"
    || !!doc.is_wdt;
  if (wdt && String(doc.status || "") !== "draft") {
    return pill("Brak dowodu VIES", "#f59e0b", "rgba(245,158,11,.15)", "shield-alert",
      "Brak dowodu VIES z dnia wystawienia — ten dokument WDT wyszedł ze stawką 0 % bez zapisanego potwierdzenia");
  }
  // brak zapisanego wyniku → sprawdzenie na żywo (jak na zamówieniu), z prefiksem kraju nabywcy
  return _buildViesBadgePlaceholder(doc.buyer_tax_no, doc.buyer_country);
}

function _hydrateViesBadges(container?: ParentNode | null): void {
  const root: ParentNode = container || document;
  const badges = root.querySelectorAll<ViesBadgeElement>(".vies-badge-pending");
  badges.forEach((b: ViesBadgeElement) => {
    if (b._viesHydrated) return;
    b._viesHydrated = true;
    const nip = b.dataset.viesNip;
    if (!nip) {
      b.remove();
      return;
    }
    SellBaseAPI.checkVies(nip)
      .then((res: ViesCheckResult) => {
        if (res && res.valid === true) {
          b.style.background = "rgba(34,197,94,.15)";
          b.style.border = "1px solid rgba(34,197,94,.3)";
          b.style.color = "#22c55e";
          b.innerHTML = '<i data-lucide="shield-check" style="width:10px;height:10px"></i> VIES OK';
          b.title = "Numer " + nip + " aktywny w VIES" + (res.name ? " (" + res.name + ")" : "");
        } else if (res && res.valid === false) {
          b.style.background = "rgba(239,68,68,.15)";
          b.style.border = "1px solid rgba(239,68,68,.3)";
          b.style.color = "#ef4444";
          b.innerHTML = '<i data-lucide="shield-x" style="width:10px;height:10px"></i> VIES nieaktywny';
          b.title = "NIP " + nip + " nie jest aktywny w VIES";
        } else {
          b.style.color = "var(--color-text-faint)";
          b.innerHTML = '<i data-lucide="help-circle" style="width:10px;height:10px"></i> VIES niedost.';
          b.title = "Nie udało się sprawdzić VIES (" + ((res && res.error) || "brak odpowiedzi") + ")";
        }
        b.classList.remove("vies-badge-pending");
        if (typeof lucide !== "undefined") lucide.createIcons({ nodes: [b] });
      })
      .catch(() => {
        b.innerHTML = '<i data-lucide="help-circle" style="width:10px;height:10px"></i> VIES ?';
        b.title = "Błąd sprawdzenia VIES";
        if (typeof lucide !== "undefined") lucide.createIcons({ nodes: [b] });
      });
  });
}
(window as any).SellBaseApp = (window as any).SellBaseApp || {};
window.SellBaseApp._hydrateViesBadges = _hydrateViesBadges;

// Globalny observer — gdy pojawi się nowy `.vies-badge-pending`, automatycznie hydrate.
(function _installViesObserver(): void {
  const winExt = window as unknown as { __viesObserverInstalled?: boolean };
  if (typeof MutationObserver !== "function" || winExt.__viesObserverInstalled) return;
  winExt.__viesObserverInstalled = true;
  const mo = new MutationObserver((mutations: MutationRecord[]) => {
    let needsHydrate = false;
    for (let i = 0; i < mutations.length && !needsHydrate; i++) {
      const added = mutations[i].addedNodes;
      for (let j = 0; j < added.length; j++) {
        const n = added[j];
        if (n.nodeType !== 1) continue;
        const el = n as Element;
        if (el.classList && el.classList.contains("vies-badge-pending")) {
          needsHydrate = true;
          break;
        }
        if (el.querySelector && el.querySelector(".vies-badge-pending")) {
          needsHydrate = true;
          break;
        }
      }
    }
    if (needsHydrate) _hydrateViesBadges(document);
  });
  if (document.body) mo.observe(document.body, { childList: true, subtree: true });
  else
    document.addEventListener("DOMContentLoaded", () => {
      mo.observe(document.body, { childList: true, subtree: true });
    });
})();

function _vatBadgeHtml(nipStatus: string): string {
  if (!nipStatus) return "";
  if (nipStatus === "Czynny")
    return '<span class="badge" style="background:#16A34A15;color:#16A34A;font-size:10px;padding:1px 6px;border-radius:4px;font-weight:600">VAT</span>';
  if (nipStatus === "Zwolniony")
    return '<span class="badge" style="background:#9CA3AF15;color:#9CA3AF;font-size:10px;padding:1px 6px;border-radius:4px;font-weight:600">Zwolniony</span>';
  return '<span class="badge" style="background:#EF444415;color:#EF4444;font-size:10px;padding:1px 6px;border-radius:4px;font-weight:600">Nieczynny</span>';
}

// ── EXPOSE: extracted functions (legacy backward-compat) ──
declare global {
  interface Window {
    _buildViesBadgePlaceholder?: typeof _buildViesBadgePlaceholder;
    _checkOrderNipStatus?: typeof _checkOrderNipStatus;
    _hydrateViesBadges?: typeof _hydrateViesBadges;
    _nipInitInlineButton?: typeof _nipInitInlineButton;
    _nipInitAutoLookup?: typeof _nipInitAutoLookup;
    _renderNipComparison?: typeof _renderNipComparison;
    _vatBadgeHtml?: typeof _vatBadgeHtml;
  }
}

/**
 * 2026-09-03 (user: „przy dodawaniu nowego klienta też niech będzie od razu pobieranie
 * danych z GUS po wpisaniu NIP"): po wpisaniu / wklejeniu 10 cyfr dane pobierają się
 * SAME — bez klikania lupy (ta zostaje jako ręczne ponowienie). Ten sam NIP nie jest
 * odpytywany dwa razy; nieudane pobranie zdejmuje blokadę (patrz catch wyżej).
 * Wymaga wcześniejszego _nipInitInlineButton na tym samym polu (klika jego lupę).
 */
function _nipInitAutoLookup(nipInputId: string, delayMs: number = 350): void {
  const input = document.getElementById(nipInputId) as HTMLInputElement | null;
  if (!input || input.dataset.nipAuto) return;
  input.dataset.nipAuto = "1";
  let timer: number | null = null;
  const fire = (): void => {
    const nip = (input.value || "").replace(/\D/g, "");
    if (nip.length !== 10 || input.dataset.nipLast === nip) return;
    const btn = input.parentElement
      ? input.parentElement.querySelector<HTMLButtonElement>(".nip-lookup-btn")
      : null;
    if (!btn || btn.disabled) return;
    input.dataset.nipLast = nip;
    btn.click();
  };
  const schedule = (): void => {
    if (timer !== null) window.clearTimeout(timer);
    timer = window.setTimeout(fire, delayMs);
  };
  input.addEventListener("input", schedule);
  input.addEventListener("paste", () => { window.setTimeout(schedule, 0); });
  input.addEventListener("blur", fire);
}

(window as any).SellBaseApp = (window as any).SellBaseApp || {};
window._nipInitAutoLookup = _nipInitAutoLookup;
window.SellBaseApp._nipInitAutoLookup = _nipInitAutoLookup;
window._buildViesBadgePlaceholder = _buildViesBadgePlaceholder;
window.SellBaseApp._buildViesBadgePlaceholder = _buildViesBadgePlaceholder;
window._checkOrderNipStatus = _checkOrderNipStatus;
window.SellBaseApp._checkOrderNipStatus = _checkOrderNipStatus;
window._hydrateViesBadges = _hydrateViesBadges;
window.SellBaseApp._hydrateViesBadges = _hydrateViesBadges;
(window as any)._viesPillDokumentu = _viesPillDokumentu;
window.SellBaseApp._viesPillDokumentu = _viesPillDokumentu;
(window as any)._numerVatUe = _numerVatUe;
(window as any)._notkaVatUe = _notkaVatUe;
window._nipInitInlineButton = _nipInitInlineButton;
window.SellBaseApp._nipInitInlineButton = _nipInitInlineButton;
window._renderNipComparison = _renderNipComparison;
window.SellBaseApp._renderNipComparison = _renderNipComparison;
window._vatBadgeHtml = _vatBadgeHtml;
window.SellBaseApp._vatBadgeHtml = _vatBadgeHtml;

// ── ES Module exports ──
export {
  _buildViesBadgePlaceholder,
  _checkOrderNipStatus,
  _hydrateViesBadges,
  _viesPillDokumentu,
  _numerVatUe,
  _notkaVatUe,
  _nipInitInlineButton,
  _nipInitAutoLookup,
  _renderNipComparison,
  _vatBadgeHtml,
};
